/**
 * Score-history sparkline — a compact SVG line chart that lives above the
 * Capital Readiness Report header. Visualises the last N reruns so the
 * founder *sees* their progress instead of just a static number.
 *
 * Props:
 *   history     – [{score: 0-100, at: ISO timestamp}, …]  (asc by time)
 *   currentScore – the score shown in the header (may differ from the most
 *                   recent history point during an in-flight rerun)
 *
 * Renders nothing if there are fewer than 2 historical points (sparklines
 * with one dot are meaningless and just clutter the header).
 */
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

export default function ScoreHistorySparkline({ history, currentScore }) {
  if (!Array.isArray(history) || history.length < 2) return null;

  const points = history
    .filter((p) => Number.isFinite(p?.score))
    .map((p) => ({ score: Math.max(0, Math.min(100, Number(p.score))), at: p.at }));
  if (points.length < 2) return null;

  // SVG geometry — wide-and-short feel that fits next to the score header.
  const W = 220;
  const H = 56;
  const PAD_X = 6;
  const PAD_Y = 6;
  const minScore = Math.max(0, Math.min(...points.map((p) => p.score)) - 5);
  const maxScore = Math.min(100, Math.max(...points.map((p) => p.score)) + 5);
  const range = maxScore - minScore || 1;

  const x = (i) => PAD_X + (i / (points.length - 1)) * (W - PAD_X * 2);
  const y = (s) => H - PAD_Y - ((s - minScore) / range) * (H - PAD_Y * 2);

  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p.score).toFixed(1)}`)
    .join(" ");
  const areaPath = `${path} L ${x(points.length - 1).toFixed(1)} ${H - PAD_Y} L ${x(0).toFixed(1)} ${H - PAD_Y} Z`;

  // Trend: compare first vs last
  const first = points[0].score;
  const last = points[points.length - 1].score;
  const delta = last - first;
  const Trend = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const trendCls = delta > 0 ? "text-emerald-700" : delta < 0 ? "text-rose-700" : "text-neutral-500";

  return (
    <div
      data-testid="score-history-sparkline"
      className="inline-flex items-center gap-3 rounded-xl border border-neutral-200 bg-white px-3 py-2"
    >
      <div className="flex flex-col">
        <p className="text-[9px] font-bold uppercase tracking-wider text-neutral-500">
          Score history · last {points.length} runs
        </p>
        <p
          data-testid="score-history-delta"
          className={"mt-0.5 inline-flex items-center gap-1 text-xs font-bold " + trendCls}
        >
          <Trend className="h-3 w-3" />
          {delta > 0 ? `+${delta}` : delta} pts since first
        </p>
      </div>
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        className="overflow-visible"
        aria-label={`Score history line chart, ${points.length} runs, current score ${currentScore || last}`}
      >
        <defs>
          <linearGradient id="sparklineFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#0A8B85" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#0A8B85" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#sparklineFill)" />
        <path d={path} fill="none" stroke="#0A8B85" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <circle
            key={p.at || `pt-${i}-${p.score}`}
            cx={x(i)}
            cy={y(p.score)}
            r={i === points.length - 1 ? 3.5 : 2}
            fill={i === points.length - 1 ? "#0A8B85" : "#fff"}
            stroke="#0A8B85"
            strokeWidth="1.5"
          >
            <title>{`${p.score}/100 · ${p.at ? new Date(p.at).toLocaleDateString() : ""}`}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
}
